import { Injectable, OnInit } from '@angular/core';
import { CanActivate, NavigationEnd, Router } from '@angular/router';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable, Subscription, catchError, filter, map, of } from 'rxjs';
import { ETipoAcesso } from 'src/app/app-navigation';
import { IAcesso } from '../api/acessos.service';
import { TermosDeUsoService } from '../api/termos-de-uso.service';
import { ConstantsRotasDoSistema } from '../constants/constants-rotas-do-sistema';

export interface IUser {
    email: string;
    first_name: string;
    last_name: string;
    id: string;
    username: string;
}

// CONSTANTS DO SISTEMA
const TIPO_ACESSO = 'tipoAcesso';
const USER = 'user';
const ACESSO = 'acesso';
const PLAY = 'play';
const AUTOCADASTRO = 'autocadastro';
const SHOW_TERMOS = 'showTermos';
const DETALHE_ACESSO = 'detalheAcesso';

export type ITheme = 'dark' | 'light';

@Injectable()
export class AuthService implements OnInit {
    private acessoSubject = new BehaviorSubject<IAcesso>({} as IAcesso);
    acessoSubject$ = this.acessoSubject.asObservable();

    private themeSubject = new BehaviorSubject<ITheme>('light');
    themeSubject$ = this.themeSubject.asObservable();

    private theme: 'dark' | 'light' = 'light';

    private _isTelaAutocadastro = false;

    private sessaoValida = false;

    constructor(
        private router: Router,
        private apiService: ApiService,
        private termosDeUsoService: TermosDeUsoService
    ) {}

    ngOnInit(): void {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this._isTelaAutocadastro = false;
        });
    }

    get loggedIn(): boolean {
        const user = localStorage.getItem(USER);
        return !!user;
    }

    get perfilSelecionado(): boolean {
        const acesso = localStorage.getItem(ACESSO);
        return !!acesso;
    }

    get isTelaDeJogada(): boolean {
        const currentPath = this.router.url;
        return currentPath.includes(PLAY);
    }

    get isTelaAutocadastro(): boolean {
        const currentPath = this.router.url;
        return currentPath.includes(AUTOCADASTRO);
    }

    get isTelaAceiteTermos(): boolean {
        const currentPath = this.router.url;
        return currentPath.includes(ConstantsRotasDoSistema.ACEITE_DE_TERMOS);
    }

    get showTermosDeUso(): boolean {
        const showTermos = localStorage.getItem(SHOW_TERMOS);
        if (showTermos) return JSON.parse(showTermos);
        else return false;
    }

    setUser(user: IUser) {
        localStorage.setItem(USER, JSON.stringify(user));
    }

    setShowAceiteTermos() {
        localStorage.setItem(SHOW_TERMOS, JSON.stringify(true));
    }

    removeShowAceiteTermos() {
        localStorage.removeItem(SHOW_TERMOS);
    }

    setAcesso(acesso: string) {
        localStorage.setItem(ACESSO, acesso);
    }

    setTipoAcesso(tipoAcesso: string) {
        localStorage.setItem(TIPO_ACESSO, tipoAcesso);
    }

    setDetalheAcesso(detalheAcesso: any) {
        localStorage.setItem(DETALHE_ACESSO, JSON.stringify(detalheAcesso));
    }

    getAcesso() {
        return localStorage.getItem(ACESSO);
    }

    getTipoAcesso(): ETipoAcesso {
        return localStorage.getItem(TIPO_ACESSO) as ETipoAcesso;
    }

    getDetalheAcesso() {
        return JSON.parse(localStorage.getItem(DETALHE_ACESSO) || '');
    }

    /**
     * Verifica se a sessão é válida chamando a API.
     * Se o cookie de autenticação não existir ou estiver expirado,
     * a API retornará 401 e consideraremos o usuário deslogado.
     */
    verificarSessao(): Observable<boolean> {
        if (!this.loggedIn) {
            this.sessaoValida = false;
            return of(false);
        }

        if (this.sessaoValida) {
            return of(true);
        }

        return this.apiService.get('acessos').pipe(
            map(() => {
                this.sessaoValida = true;
                return true;
            }),
            catchError(() => {
                this.sessaoValida = false;
                return of(false);
            })
        );
    }

    logIn(email: string, password: string) {
        return new Observable((observer) => {
            this.apiService.post('autenticar', { username: email, senha: password }).subscribe({
                next: (response: any) => {
                    this.sessaoValida = true;
                    this.setUser(response.user);

                    observer.next(response);
                },
                error: (e: any) => {
                    observer.error(e);
                },
                complete: () => {
                    observer.complete();
                }
            });
        });
    }

    setTheme(theme: 'dark' | 'light') {
        this.themeSubject.next(theme);
    }

    public createAccount(usuario: any) {
        return this.apiService.post('acessos-pessoais', usuario);
    }

    public changePassword(senha: string, token: string) {
        return new Observable((observer) => {
            this.apiService
                .post('alterar-senha', {
                    token,
                    senha
                })
                .subscribe({
                    next: (response) => {
                        observer.next(response);
                    },
                    error: (error) => {
                        observer.error(error);
                    }
                });
        });
    }

    public sendRecoverEmail(email: string) {
        return new Observable((observer) => {
            this.apiService.post('solicitar-alteracao-senha', { email }).subscribe(
                (response) => {
                    observer.next(response);
                },
                (error) => {
                    observer.error(error);
                }
            );
        });
    }

    /**
     * Encerra a sessão no backend (invalida o cookie), limpa os dados locais
     * e redireciona para o login.
     */
    logOut(): Subscription {
        this.sessaoValida = false;
        localStorage.clear();
        this.goToLogin();

        return this.apiService
            .post('logout', {})
            .pipe(catchError(() => of(null)))
            .subscribe();
    }

    goToLogin() {
        this.router.navigate([
            ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.LOGIN
        ]);
    }

    private navegarParaDashboard() {
        this.router.navigate([ConstantsRotasDoSistema.DEFAULT_PATH]);
    }

    private navegarParaAceiteTermos() {
        this.router.navigate([
            ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.ACEITE_DE_TERMOS
        ]);
    }

    private setarLocalStorageTermos() {
        this.setShowAceiteTermos();
    }

    private salvarDadosDeAcesso(acesso: IAcesso) {
        this.setAcesso(acesso.uuid);
        this.setTipoAcesso(acesso.tipo);
        this.setDetalheAcesso(acesso.detalhe);
        this.acessoSubject.next(acesso);
    }

    private obterAceiteDeTermos(acesso: IAcesso) {
        this.termosDeUsoService.get().subscribe({
            next: (responseTermos) => {
                this.salvarDadosDeAcesso(acesso);

                if (responseTermos.resultados.length > 0) {
                    this.setarLocalStorageTermos();
                    this.navegarParaAceiteTermos();
                } else {
                    this.navegarParaDashboard();
                }
            }
        });
    }

    public selecionarAcesso(acesso: IAcesso) {
        this.sessaoValida = true;

        if (acesso.tipo == ETipoAcesso.ADMINISTRADOR) {
            this.navegarParaDashboard();
            this.salvarDadosDeAcesso(acesso);
        } else {
            this.obterAceiteDeTermos(acesso);
        }

        if (acesso.tipo != ETipoAcesso.PESSOAL) {
            this.setTheme('light');
        }
    }
}

@Injectable()
export class AuthGuardService implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService
    ) {}

    canActivate(): Observable<boolean> {
        return this.authService.verificarSessao().pipe(
            map((logado) => {
                if (!logado) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.LOGIN
                    ]);
                    return false;
                }

                if (!this.authService.perfilSelecionado) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.ACESSOS
                    ]);
                    return false;
                }

                if (this.authService.showTermosDeUso && !this.authService.isTelaAceiteTermos) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.ACEITE_DE_TERMOS
                    ]);
                    return false;
                }

                return true;
            })
        );
    }
}

@Injectable()
export class AuthGuardServiceAceiteTermos implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService
    ) {}

    canActivate(): Observable<boolean> {
        return this.authService.verificarSessao().pipe(
            map((logado) => {
                if (!logado) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.LOGIN
                    ]);
                    return false;
                }

                if (!this.authService.perfilSelecionado) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.ACESSOS
                    ]);
                    return false;
                }

                return true;
            })
        );
    }
}

@Injectable()
export class AuthGuardServiceAcesso implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService
    ) {}

    canActivate(): Observable<boolean> {
        return this.authService.verificarSessao().pipe(
            map((logado) => {
                if (!logado) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.LOGIN
                    ]);
                    return false;
                }

                if (this.authService.getAcesso()) {
                    this.router.navigate([ConstantsRotasDoSistema.DEFAULT_PATH]);
                    return false;
                }

                return true;
            })
        );
    }
}

@Injectable()
export class AuthGuardServiceLoginForm implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService
    ) {}

    canActivate(): Observable<boolean> {
        return this.authService.verificarSessao().pipe(
            map((logado) => {
                if (!logado) return true;

                const perfilSelecionado = this.authService.perfilSelecionado;
                const showTermos = this.authService.showTermosDeUso;

                if (perfilSelecionado && showTermos) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.TERMOS_DE_USO
                    ]);
                } else if (perfilSelecionado) {
                    this.router.navigate([ConstantsRotasDoSistema.DEFAULT_PATH]);
                } else {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.ACESSOS
                    ]);
                }

                return false;
            })
        );
    }
}

@Injectable({ providedIn: 'root' })
export class RedirectGuard implements CanActivate {
    constructor(
        private router: Router,
        private authService: AuthService
    ) {}

    canActivate(): Observable<boolean> {
        return this.authService.verificarSessao().pipe(
            map((logado) => {
                if (!logado) {
                    this.router.navigate([
                        ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.LOGIN
                    ]);
                    return false;
                }

                const perfil = this.authService.getTipoAcesso();
                switch (perfil) {
                    case ETipoAcesso.ADMINISTRADOR:
                    case ETipoAcesso.CLIENTE:
                        this.router.navigate([ConstantsRotasDoSistema.DASHBOARD]);
                        break;
                    case ETipoAcesso.PESSOAL:
                        this.router.navigate([ConstantsRotasDoSistema.LICENCAS]);
                        break;
                    default:
                        this.router.navigate([
                            ConstantsRotasDoSistema.DEFAULT_PATH + ConstantsRotasDoSistema.ACESSOS
                        ]);
                }

                return false;
            })
        );
    }
}
